import React, { FunctionComponent, useCallback, useMemo, useState } from 'react';
import Button from 'ui/atoms/button';
import Translate from 'src/ui/atoms/translate';
import { Modal, ModalHeader, ModalContent, ModalFooter, ModalFooterButtons } from 'ui/molecules/modal';
import { AdminProductDetail, ProductStatusEnum } from 'api/models';
import useApiCall from 'src/hooks/use-api-call';
import { AdminApi } from 'src/api';
import Input from 'src/ui/atoms/input';

interface RejectCryptoSecuritiesProps {
  productId: string;
  onUpdateProductStatus: (token: AdminProductDetail) => void;
}

const RejectCryptoSecurities: FunctionComponent<RejectCryptoSecuritiesProps> = ({
  productId,
  onUpdateProductStatus,
}) => {
  const [isModalOpen, setModalOpen] = useState(false);
  const [reason, setReason] = useState('');

  const { withApi, makeAuthenticatedApi } = useApiCall(true);
  const api: AdminApi = useMemo(() => makeAuthenticatedApi(AdminApi), [makeAuthenticatedApi]);

  const onClose = useCallback(() => {
    setModalOpen(false);
  }, []);

  const onSubmit = useCallback(() => {
    withApi(async () => {
      const token = await api.adminProductsPartialUpdate({
        id: productId,
        patchedAdminProductDetailRequest: {
          status: ProductStatusEnum.VALIDATION_REJECTED,
          validationRejectionReason: reason,
        },
      });

      setModalOpen(false);
      onUpdateProductStatus(token);
    });
  }, [withApi, api, reason]);

  return (
    <>
      <Button variant="link" onClick={() => setModalOpen(true)}>
        <Translate name="issuerProductDetails.cryptoSecuritiesValidation.reject" />
      </Button>
      {isModalOpen && (
        <Modal onClose={onClose}>
          <ModalHeader>
            <Translate name="issuerProductDetails.cryptoSecuritiesValidation.rejectionReason" />
          </ModalHeader>
          <ModalContent>
            <Translate name="issuerProductDetails.cryptoSecuritiesValidation.enterReason" as="p" />
            <Input onChange={(evt) => setReason((evt.target as HTMLInputElement).value)} />
          </ModalContent>
          <ModalFooter>
            <ModalFooterButtons
              actionButtons={[
                {
                  name: 'cancel',
                  content: <Translate name="common.cancel" />,
                  size: 'large',
                  onClick: onClose,
                },
                {
                  name: 'submit',
                  content: <Translate name="issuerProductDetails.cryptoSecuritiesValidation.reject" />,
                  variant: 'primary',
                  type: 'submit',
                  size: 'large',
                  onClick: onSubmit,
                  disabled: reason.length === 0,
                },
              ]}
            />
          </ModalFooter>
        </Modal>
      )}
    </>
  );
};

export default RejectCryptoSecurities;
